import {
  Alert,
  Paper,
  SimpleGrid,
  Stack,
  TextInput,
  Select,
  Switch,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useEffect, useImperativeHandle } from "react"
import { useTranslation } from "react-i18next"
import { getErrorMessage } from "../utils"
import { useCreateAdminUser, useUpdateAdminUser } from "../hooks/useAdminUsers"
import { useAgencies } from "../hooks/useAgencies"
import type { AdminUser, AdminUserFormValues } from "../models/adminUser"

interface Props {
  user?: AdminUser | null
  onSuccess: () => void
}

const AdminUserFormContent = forwardRef<{ submit: () => void }, Props>(({ user, onSuccess }, ref) => {
  const { t } = useTranslation()
  const isEdit = !!user
  const createMutation = useCreateAdminUser()
  const updateMutation = useUpdateAdminUser()
  const { data: agencies = [] } = useAgencies()

  const form = useForm<AdminUserFormValues>({
    initialValues: {
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      agency_id: 0 as unknown as number,
      status: 10,
      type: 1,
      daily_newsletter_enabled: true,
      agency_visit_newsletter_enabled: false,
    },
    validate: {
      username: (v) => (v?.length >= 2 ? null : t("adminUsers.validation.usernameRequired")),
      first_name: (v) => (v?.length ? null : t("adminUsers.validation.firstNameRequired")),
      last_name: (v) => (v?.length ? null : t("adminUsers.validation.lastNameRequired")),
      email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : t("adminUsers.validation.emailInvalid")),
      agency_id: (v) => (v && v > 0 ? null : t("adminUsers.validation.agencyRequired")),
      ...(isEdit ? {} : {
        password: (v: string) => (v?.length >= 6 ? null : t("adminUsers.validation.passwordMin")),
      }),
    },
  })

  useEffect(() => {
    if (user) {
      form.setValues({
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name ?? "",
        email: user.email,
        password: "",
        agency_id: user.agency_id,
        status: user.status,
        type: user.type,
        daily_newsletter_enabled: user.daily_newsletter_enabled,
        agency_visit_newsletter_enabled: user.agency_visit_newsletter_enabled,
      })
    }
  }, [user])

  const handleSubmit = (values: AdminUserFormValues) => {
    if (isEdit && user) {
      const { password, ...rest } = values
      updateMutation.mutate(
        { id: user.id, values: rest },
        {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminUsers.updateSuccess"),
            })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        },
      )
    } else {
      createMutation.mutate(values, {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("adminUsers.createSuccess"),
          })
          onSuccess()
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const handleFormSubmit = form.onSubmit(handleSubmit)
  useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

  return (
    <form onSubmit={handleFormSubmit}>
      {(createMutation.isError || updateMutation.isError) && (
        <Alert
          color="red"
          mb="md"
          withCloseButton
          onClose={() => {
            createMutation.reset()
            updateMutation.reset()
          }}
        >
          {createMutation.error?.message || updateMutation.error?.message}
        </Alert>
      )}
      <Paper withBorder p="md" radius="md">
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("adminUsers.username")}
              {...form.getInputProps("username")}
              autoFocus={!isEdit}
            />
            <TextInput
              label={t("adminUsers.email")}
              {...form.getInputProps("email")}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label={t("adminUsers.firstName")}
              {...form.getInputProps("first_name")}
            />
            <TextInput
              label={t("adminUsers.lastName")}
              {...form.getInputProps("last_name")}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Select
              label={t("adminUsers.agency")}
              placeholder={t("adminUsers.selectAgency")}
              data={agencies.map((a) => ({ value: String(a.id), label: a.name }))}
              searchable
              {...form.getInputProps("agency_id")}
              onChange={(v) => form.setFieldValue("agency_id", v ? Number(v) : 0)}
            />
            <Select
              label={t("adminUsers.type")}
              data={[
                { value: "1", label: "GPA" },
                { value: "2", label: "BEST" },
              ]}
              {...form.getInputProps("type")}
              onChange={(v) => form.setFieldValue("type", v ? Number(v) : 1)}
            />
          </SimpleGrid>
          <Select
            label={t("adminUsers.status")}
            data={[
              { value: "10", label: t("adminUsers.active") },
              { value: "2", label: t("adminUsers.suspended") },
              { value: "0", label: t("adminUsers.inactive") },
            ]}
            {...form.getInputProps("status")}
            onChange={(v) => form.setFieldValue("status", v ? Number(v) : 10)}
          />
          {!isEdit && (
            <TextInput
              label={t("adminUsers.password")}
              type="password"
              autoComplete="new-password"
              {...form.getInputProps("password")}
            />
          )}
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <Switch
              label={t("adminUsers.dailyNewsletter")}
              {...form.getInputProps("daily_newsletter_enabled", { type: "checkbox" })}
            />
            <Switch
              label={t("adminUsers.agencyVisitNewsletter")}
              {...form.getInputProps("agency_visit_newsletter_enabled", { type: "checkbox" })}
            />
          </SimpleGrid>
        </Stack>
      </Paper>
    </form>
  )
})

export default AdminUserFormContent
